import { useEffect, useState } from 'react';
import { Card, Col, Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import CheckmarkCell from '../../../../../components/tables/cellComponents/CheckmarkCell';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useGetOrganizationQuery,
  useDeleteOrganizationMutation,
} from '../../../../../services/endpoints/places/organization';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCOrganization } from '../../../../../types/place.types';
import { formatDate } from '../../../../../utils/dateUtils';
import { canViewOrganizations } from '../../../../../utils/rbac';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { getErrorMessage } from '../../../../../utils/utils';

const currentUser = getCurrentUser();

export const OrganizationDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const userHasAccess = canViewOrganizations(currentUser);
  const { data, error, isLoading } = useGetOrganizationQuery(+params.id!, {
    skip: !userHasAccess
  });
  const [deleteOrganization] = useDeleteOrganizationMutation();
  const [organization, setOrganization] = useState<SFCOrganization>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading organization', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setOrganization(data.organization);
    }
  }, [data]);

  const handleConfirmDelete = async () => {
    try {
      await deleteOrganization(+params.id!).unwrap();
      setShowModal(false);
      navigate('/admin/places/organizations');
      addToast('Organization deleted successfully', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (isLoading || !organization) {
    return (
        <div className="w-100 vh-100 d-inline-flex">
          <Spinner
              variant="primary"
              className="m-auto"
          />
        </div>
    );
  }

  return (
      <>
        <div
            className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
            style={{ background: '#EFEFEF' }}
        >
          <Stack
              direction="horizontal"
              gap={2}
              className="ps-lg-3 ps-0"
          >
            <button
                className="btn btn-link text-info p-0"
                onClick={() => navigate('/admin/places/organizations')}
            >
              <FaArrowLeft
                  size={20}
                  className="d-lg-none"
              />
              <FaArrowLeft
                  size={24}
                  className="d-none d-lg-block"
              />
            </button>
            <h2
                className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
                style={{ color: '#888888', fontSize: '1rem' }}
            >
              Organizations List
            </h2>
            <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Organization #${organization?.id}`}</h2>
          </Stack>
          <Stack
              direction="horizontal"
              gap={1}
              className="d-none d-lg-flex"
          >
            <SimpleButton
                variant="primary"
                height={36}
                onClick={() => navigate(`/admin/places/organizations/${organization?.id}/edit`)}
            >
              Edit organization
            </SimpleButton>
            <SimpleButton
                variant="primary"
                height={36}
                onClick={() => setShowModal(true)}
            >
              Delete organization
            </SimpleButton>
          </Stack>
        </div>
        <div>
          <Stack
              gap={4}
              className="p-4"
          >
            <DetailSection title="Details">
              <LabelText label="Name:">{organization?.name}</LabelText>
              <LabelText label="Created At:">
                {formatDate(new Date(organization?.created_at), 'MMM dd, yyyy hh:mm a')}
              </LabelText>
              <LabelText label="Location:">
                <LinkCell
                    content={organization?.location?.title ?? 'No location title'}
                    url={`/admin/places/locations/${organization?.location?.id}`}
                />
              </LabelText>
              <LabelText label="Venues:">
                <LinkCell
                    content={`${organization?.venues.length} Venue${organization?.venues.length !== 1 ? 's' : ''}`}
                    url={`/admin/places/venues?organization_id=${organization?.id}`}
                />
              </LabelText>
            </DetailSection>

            <DetailSection title="Contacts">
              {organization.contacts.map((c) => (
                  <Col
                      md={6}
                      lg={5}
                      xl={4}
                      xxl={3}
                  >
                    <Card className="text-left shadow">
                      <Card.Body>
                        <Card.Text className={'h2 text-capitalize'}>{c.full_name}</Card.Text>
                        <br />
                        <LabelText label="Email:">{c.email}</LabelText>
                        <LabelText label="Receives Rosters:">
                          <CheckmarkCell value={c.should_be_sent_roster} />
                        </LabelText>
                        <LabelText label="Receives Invoices:">
                          <CheckmarkCell value={c.should_be_sent_invoices} />
                        </LabelText>
                      </Card.Body>
                    </Card>
                  </Col>
              ))}
            </DetailSection>
          </Stack>
        </div>

        <ConfirmationModal
            show={showModal}
            title="Delete Organization"
            message="Are you sure you want to delete this organization?"
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowModal(false)}
        />
      </>
  );
};